/**
 * Turn any image URL into a browser-loadable one.
 *
 * - Absolute (http/https/data) URLs pass through.
 * - CMS URLs like `/api/files/<id>` are resolved against
 *   REACT_APP_BACKEND_URL and get an optional `?w=<pixels>` hint so the
 *   backend serves the smallest resized/webp variant that fits.
 * - Everything else (static paths under `/hero/...`) is returned as-is.
 */
export function resolveImageUrl(url, width) {
  if (!url) return '';
  if (/^https?:\/\//i.test(url) || url.startsWith('data:')) return url;
  if (url.startsWith('/api/')) {
    const base = `${process.env.REACT_APP_BACKEND_URL}${url}`;
    if (width) {
      const sep = base.includes('?') ? '&' : '?';
      return `${base}${sep}w=${width}`;
    }
    return base;
  }
  return url;
}

/** Width buckets emitted by the backend image pipeline. */
export const IMAGE_WIDTHS = [400, 800, 1600];

/**
 * Build a set of `<img>` attributes that lets the browser pick the right
 * size for the current device. For CMS-managed images we emit a `srcSet`
 * across the width buckets the backend generates; for static images we
 * fall through with just `src` because there are no variants to point at.
 *
 * `sizes` follows the standard media-query syntax, e.g.
 *   "(min-width: 1024px) 33vw, 100vw"
 * If omitted we default to `100vw`.
 */
export function imageAttrs(url, { sizes, fallbackWidth = 800 } = {}) {
  if (!url) return { src: '' };
  const isCms = url.startsWith('/api/');
  if (!isCms) {
    return { src: resolveImageUrl(url, fallbackWidth) };
  }
  const srcSet = IMAGE_WIDTHS
    .map((w) => `${resolveImageUrl(url, w)} ${w}w`)
    .join(', ');
  return {
    src: resolveImageUrl(url, fallbackWidth),
    srcSet,
    sizes: sizes || '100vw',
  };
}
